import { Inject, Injectable, NgZone } from '@angular/core';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';

import { VIDEO_SESSION_ACTIVE } from '@sondermind/constants';
import { LocalStorageService } from '@sondermind/local-storage';
import { IAuthService, AUTH_SERVICE } from '@sondermind/utilities/models-auth';

@Injectable({
  providedIn: 'root'
})

export class IdleDetectionService {
  constructor(
    private idle: Idle,
    private localStorageService: LocalStorageService,
    private ngZone: NgZone,
    @Inject(AUTH_SERVICE)
    private authService: IAuthService,
  ) {}

  /**
   * Initializes @ng-idle so we can detect when the application user
   * has been inactive for too long. Sets the "interrupt sources" that should
   * be used to detect activity; default sources include mouse movement,
   * scrolling, etc.
   *
   * DO NOT time out if the user is in an active video session.
   *
   * @param idleTimer    Time in seconds that a user can be inactive
   *                     before being considered "idle".
   * @param timeoutTimer Time in seconds that a user can be "idle"
   *                     before being considered "inactive".
   * @param onTimeout    Function to call when the user is
   *                     declared to be "inactive".
   * @see {@link <https://github.com/moribvndvs/ng2-idle> for ng-idle docs}
   */
  initialize(
    idleTimer: number,
    timeoutTimer: number,
    onTimeout: () => void
  ): void {
    if (!this.authService.isIdleCheckEnabled()) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.idle.setIdle(idleTimer);
      this.idle.setTimeout(timeoutTimer);
      this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

      this.idle.onTimeout.subscribe((value) => {
        this.ngZone.run(() => {
          if (this.localStorageService.getItem(VIDEO_SESSION_ACTIVE)) {
            // To help debug video session issues
            // eslint-disable-next-line no-console
            console.error('Unexpected idle timeout in video session, value ', value);
            // Idle should be stopped during a video session
            this.stopIdleDetection();
          } else {
            onTimeout();
          }
        });
      });
    });
  }

  /**
   * Starts and/or resets the idle detection process.
   */
  resetIdleDetection(): void {
    if (!this.authService.isIdleCheckEnabled()) {
      return;
    }

    this.ngZone.runOutsideAngular(() => this.idle.watch());
  }

  /**
   * Stops the idle detection process in the currently open tab.
   */
  stopIdleDetection(): void {
    this.idle.stop();
  }
}
